import { useState, useRef, useEffect } from "react";
import { BiCheck, BiChevronDown } from "react-icons/bi";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "./pagination";

function CustomSelect({
  pgdropdown,
  value,
  onValueChange,
  options = [10, 50, 100],
  total,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef(null);

  const toggleDropdown = () => setIsOpen(!isOpen);

  const handleSelect = (option) => {
    if (!total || option <= total) {
      onValueChange(Number(option));
      setIsOpen(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <section
      ref={selectRef}
      className={`tw-relative tw-inline-block tw-w-[70px] ${pgdropdown} `}
    >
      <button
        type="button"
        onClick={toggleDropdown}
        className="tw-bg-transaparent tw-w-[70px] tw-flex tw-items-center tw-justify-between tw-w-full tw-px-2 tw-py-2 tw-text-sm tw-border tw-rounded-md tw-shadow-sm hover:tw-bg-opacity-50 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-blue-500"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="tw-block tw-truncate">{value}</span>
        <BiChevronDown className="tw-w-4 tw-h-4 tw-ml-1" />
      </button>

      {isOpen && (
        <div className="tw-w-full tw-absolute tw-z-10 tw-mt-1 tw-bg-white tw-border tw-rounded-md tw-shadow-lg tw-max-h-40 tw-overflow-auto focus:tw-outline-none">
          <div
            className="tw-py-1 tw-list-none"
            role="listbox"
            aria-labelledby="select-button"
          >
            {options?.map((option, index) => {
              const isDisabled = total && option > total;
              return (
                <li
                  key={index}
                  onClick={() => !isDisabled && handleSelect(option)}
                  className={`tw-w-full tw-select-none tw-relative tw-py-2 tw-text-sm hover:tw-bg-blue-100 tw-text-black tw-space-x-3 ${
                    value === option.toString() ? "tw-bg-blue-50" : ""
                  } ${
                    isDisabled
                      ? "tw-opacity-50 tw-cursor-not-allowed tw-text-gray-400"
                      : "tw-cursor-pointer"
                  }`}
                  role="option"
                  aria-selected={value === option.toString()}
                  aria-disabled={isDisabled}
                >
                  <span className="tw-text-black tw-font-medium tw-text-sm tw-pl-2">
                    {option}
                  </span>
                  {value === option.toString() && (
                    <span className="tw-absolute tw-inset-y-0 tw-right-0 tw-flex tw-items-center tw-pr-4">
                      <BiCheck className="tw-w-4 tw-h-4 tw-text-blue-600" />
                    </span>
                  )}
                </li>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}

// CustomSelect.propTypes = {
//   value: PropTypes.string.isRequired,
//   onValueChange: PropTypes.func.isRequired,
//   options: PropTypes.arrayOf(PropTypes.number),
// };

function UsePagination({
  totalPages,
  currentPage,
  onPageChange,
  limit,
  onLimitChange,
  total,
  className,
  pgdrop,
  showtotal,
  mainpagination,
}) {
  const visiblePages = [1, 2, 3];
  const showEllipsis = totalPages > 3;

  return (
    <main>
      <div className={` ${className} partition`}></div>
      <section className="tw-px-2 tw-flex tw-tems-center tw-justify-end md:tw-justify-between">
        <div className={`tw-hidden md:tw-flex tw-items-center tw-gap-x-2  `}>
          <CustomSelect
            pgdropdown={pgdrop}
            value={limit ? limit.toString() : 0}
            onValueChange={onLimitChange}
            options={[10, 50, 100]}
            total={total}
          />

          <div className={showtotal}>
            {" "}
            <b>Total</b> {total}{" "}
          </div>
        </div>
        <Pagination className={mainpagination}>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                className={`${
                  currentPage <= 1
                    ? " tw-opacity-30 tw-cursor-not-allowed"
                    : "tw-cursor-pointer"
                }`}
                href={"#" + currentPage}
                onClick={() => onPageChange(Math.max(currentPage - 1, 1))}
                disabled={currentPage < 2}
              />
            </PaginationItem>
            {totalPages > 3
              ? visiblePages.map((page) => (
                  <PaginationItem key={page}>
                    <PaginationLink
                      className={`tw-w-8 tw-h-8 tw-p-0 tw-flex tw-items-center tw-justify-center ${
                        currentPage === page
                          ? "tw-bg-[#BEC663] tw-rounded-full tw-text-white"
                          : ""
                      }`}
                      href={"#" + currentPage}
                      onClick={() => onPageChange(page)}
                      isActive={currentPage === page}
                    >
                      {page}
                    </PaginationLink>
                  </PaginationItem>
                ))
              : [...Array(totalPages)].map((_, index) => (
                  <PaginationItem key={index + 1}>
                    <PaginationLink
                      className={`tw-w-8 tw-h-8 tw-p-0 tw-flex tw-items-center tw-justify-center ${
                        currentPage === index + 1
                          ? "tw-bg-[#BEC663] tw-rounded-full tw-text-white"
                          : ""
                      }`}
                      href={"#" + currentPage}
                      onClick={() => onPageChange(index + 1)}
                      isActive={currentPage === index + 1}
                    >
                      {index + 1}
                    </PaginationLink>
                  </PaginationItem>
                ))}
            {showEllipsis && (
              <PaginationItem>
                <PaginationEllipsis />
              </PaginationItem>
            )}
            {currentPage > 3 && (
              <PaginationItem>
                <PaginationLink
                  className={`tw-w-8 tw-h-8 tw-p-0 tw-flex tw-items-center tw-justify-center ${
                    currentPage
                      ? "tw-bg-[#BEC663] tw-rounded-full tw-text-white"
                      : ""
                  }`}
                  href={"#" + currentPage}
                  onClick={() => onPageChange(currentPage)}
                  isActive
                >
                  {currentPage}
                </PaginationLink>
              </PaginationItem>
            )}
            {currentPage < totalPages - 1 && (
              <PaginationItem>
                <PaginationLink
                  className="tw-w-8 tw-h-8 tw-p-0 tw-flex tw-items-center tw-justify-center"
                  href="#"
                  onClick={() => onPageChange(currentPage + 1)}
                >
                  {currentPage + 1}
                </PaginationLink>
              </PaginationItem>
            )}

            <PaginationItem>
              <PaginationNext
                className={`${
                  currentPage === totalPages
                    ? " tw-opacity-30 tw-cursor-not-allowed"
                    : "tw-cursor-pointer"
                }`}
                href="#"
                onClick={() =>
                  onPageChange(Math.min(currentPage + 1, totalPages))
                }
                disabled={currentPage === totalPages}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </section>
    </main>
  );
}

// UsePagination.propTypes = {
//   totalPages: PropTypes.number,
//   currentPage: PropTypes.number,
//   onPageChange: PropTypes.func,
//   limit: PropTypes.number,
//   onLimitChange: PropTypes.func,
//   total: PropTypes.number,
//   className: PropTypes.string,
// };

export default UsePagination;
